import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DatePicker } from "@/components/DatePicker";
import { toast } from "sonner";

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function buildInitialForm({ evaluation, criteria }) {
  if (evaluation) {
    const scores = {};
    for (const k of criteria) scores[k] = evaluation.scores?.[k] ?? 5;
    for (const [k, v] of Object.entries(evaluation.scores || {})) {
      if (!(k in scores)) scores[k] = v;
    }
    return { date: evaluation.date || "", notes: evaluation.notes || "", scores };
  }
  return {
    date: todayIso(),
    notes: "",
    scores: Object.fromEntries(criteria.map(k => [k, 5])),
  };
}

// Wrapper externo: usa `key` para forçar remount sempre que abrir/trocar avaliação.
export function EvaluationDialog({ open, onOpenChange, studentId, evaluation, onSaved, criteria: criteriaProp }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="eval-dialog">
        {open && (
          <EvalForm
            key={evaluation?.id || "new"}
            studentId={studentId}
            evaluation={evaluation}
            criteriaProp={criteriaProp}
            onClose={() => onOpenChange(false)}
            onSaved={onSaved}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function EvalForm({ studentId, evaluation, criteriaProp, onClose, onSaved }) {
  const [criteria, setCriteria] = useState(criteriaProp || []);
  const [form, setForm] = useState(() => buildInitialForm({ evaluation, criteria: criteriaProp || [] }));
  const [saving, setSaving] = useState(false);
  const isEdit = !!evaluation;

  // Carrega critérios da API se não foram passados via prop
  useEffect(() => {
    if (criteriaProp?.length) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/evaluations/criteria");
        if (cancelled) return;
        setCriteria(r.data.criteria);
        // Quando os critérios chegarem, completa scores faltantes
        setForm((prev) => {
          const next = { ...prev, scores: { ...prev.scores } };
          for (const k of r.data.criteria) {
            if (next.scores[k] == null) next.scores[k] = evaluation?.scores?.[k] ?? 5;
          }
          return next;
        });
      } catch (err) {
        toast.error(formatApiError(err));
      }
    })();
    return () => { cancelled = true; };
  }, [criteriaProp, evaluation]);

  const submit = async (e) => {
    e.preventDefault();
    if (!studentId) return toast.error("Selecione um aluno");
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/evaluations/${evaluation.id}`, { student_id: studentId, ...form });
        toast.success("Avaliação atualizada");
      } else {
        await api.post("/evaluations", { student_id: studentId, ...form });
        toast.success("Avaliação registrada");
      }
      onClose();
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>{isEdit ? "Editar avaliação" : "Nova avaliação"}</DialogTitle>
        <DialogDescription>
          {isEdit
            ? "Ajuste data, notas e observações desta avaliação."
            : "Informe a data e atribua notas de 1 a 10 para cada critério técnico."}
        </DialogDescription>
      </DialogHeader>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label>Data</Label>
          <DatePicker value={form.date} onChange={(d) => setForm({ ...form, date: d })} testId="eval-date-input" />
        </div>
        <div>
          <Label>Critérios (1 a 10)</Label>
          <div className="grid sm:grid-cols-2 gap-3 mt-2">
            {criteria.map((c) => (
              <div key={c} className="flex items-center gap-3 p-2 rounded border border-border">
                <div className="flex-1 text-sm font-semibold">{c}</div>
                <Input
                  type="number" min={1} max={10}
                  value={form.scores[c] ?? ""}
                  onFocus={(e) => e.target.select()}
                  onChange={(e) => {
                    const raw = e.target.value;
                    const val = raw === "" ? "" : Math.max(1, Math.min(10, Number(raw)));
                    setForm(f => ({ ...f, scores: { ...f.scores, [c]: val } }));
                  }}
                  onBlur={(e) => {
                    if (e.target.value === "" || isNaN(Number(e.target.value))) {
                      setForm(f => ({ ...f, scores: { ...f.scores, [c]: 1 } }));
                    }
                  }}
                  className="w-20 text-center font-black"
                  data-testid={`eval-score-${c}`}
                />
              </div>
            ))}
          </div>
        </div>
        <div>
          <Label>Observações</Label>
          <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
          <Button type="submit" disabled={saving} data-testid="save-eval-button">
            {saving ? "Salvando..." : isEdit ? "Atualizar" : "Salvar"}
          </Button>
        </DialogFooter>
      </form>
    </>
  );
}

export default EvaluationDialog;
